import { client } from '@/client';
import {
  MyPhishingOverview,
  MyPhishingSimulationDetail,
} from '@/client/my-phishing';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const overviewQueryKey = 'myPhishingOverview';
const detailQueryKey = 'myPhishingSimulationDetail';

export const useMyPhishingOverview = (
  params?: UseQueryOptions<MyPhishingOverview>,
): UseQueryResult<MyPhishingOverview> & {
  overview: MyPhishingOverview | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 300000,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery<MyPhishingOverview>(
    [overviewQueryKey],
    () => client.myPhishing.getOverview(),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...restOptions,
    },
  );

  return {
    overview: data,
    ...(rest as UseQueryResult<MyPhishingOverview>),
  };
};

export const useMyPhishingSimulationDetail = (
  campaignId: string,
  params?: UseQueryOptions<MyPhishingSimulationDetail>,
): UseQueryResult<MyPhishingSimulationDetail> & {
  detail: MyPhishingSimulationDetail | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 300000,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery<MyPhishingSimulationDetail>(
    [detailQueryKey, campaignId],
    () => client.myPhishing.getSimulationDetail(campaignId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!campaignId,
      ...restOptions,
    },
  );

  return {
    detail: data,
    ...(rest as UseQueryResult<MyPhishingSimulationDetail>),
  };
};

export const useMarkSimulationViewed = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(
    'markSimulationViewed',
    (campaignId) => client.myPhishing.markViewed(campaignId),
    {
      onSuccess: (_data, campaignId) => {
        queryClient.invalidateQueries([overviewQueryKey]);
        queryClient.invalidateQueries([detailQueryKey, campaignId]);
      },
    },
  );
};
